/**
 * Data layer for `disallowed-traffic-channels-per-offer`. Thin typed fetch wrappers over /api/v1/disallowed-traffic-channels-per-offer.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DisallowedTrafficChannelsPerOfferRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDisallowedTrafficChannelsPerOfferResponse {
	count: number;
	items: DisallowedTrafficChannelsPerOfferRow[];
}

export interface CreateDisallowedTrafficChannelsPerOfferBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/disallowed-traffic-channels-per-offer";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDisallowedTrafficChannelsPerOfferResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDisallowedTrafficChannelsPerOfferResponse>(res);
}

export async function create(body: CreateDisallowedTrafficChannelsPerOfferBody): Promise<DisallowedTrafficChannelsPerOfferRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DisallowedTrafficChannelsPerOfferRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const disallowedTrafficChannelsPerOfferQueryKeys = {
	all: ["disallowed-traffic-channels-per-offer"] as const,
	list: () => [...disallowedTrafficChannelsPerOfferQueryKeys.all] as const,
	detail: (id: string) => [...disallowedTrafficChannelsPerOfferQueryKeys.all, id] as const,
};
